import { useEffect, useState } from "react";
import api, { fmtErr } from "@/lib/api";

/**
 * PurchaseHistory — V2.7.0 — lists the last 30 days of paid SKU purchases and
 * exposes a one-click "Refund" button while the 24-hour window is open.
 *
 * Self-service refunds:
 *  • call /api/payments/refund {session_id}
 *  • backend revokes the SKU, sets a 7-day rebuy cooldown, calls Stripe.
 */
export default function PurchaseHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(null); // session_id currently refunding

  async function reload() {
    setLoading(true);
    try {
      const { data } = await api.get("/payments/my-purchases");
      setRows(data || []);
    } catch (e) {
      setErr(fmtErr(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, []);

  async function refund(r) {
    if (!window.confirm(`Refund "${r.sku}" for $${r.amount.toFixed(2)}? The item will be revoked and you can't repurchase it for 7 days.`)) return;
    setBusy(r.session_id); setErr(""); setMsg("");
    try {
      await api.post("/payments/refund", { session_id: r.session_id });
      setMsg("Refund processed. Money will appear in your bank within 5–10 business days.");
      setTimeout(() => setMsg(""), 5000);
      await reload();
    } catch (e) {
      setErr(fmtErr(e.response?.data?.detail) || e.message);
      setTimeout(() => setErr(""), 5000);
    } finally { setBusy(null); }
  }

  if (loading) return <div className="text-xs text-slate-500 py-3"><i className="fa-solid fa-spinner fa-spin mr-2"/>Loading purchase history…</div>;

  return (
    <div data-testid="purchase-history" className="mt-2">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
        <i className="fa-solid fa-receipt mr-1.5 text-amber-300"/> Recent Purchases
      </div>
      {msg && <div className="text-[11px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-1.5 mb-2">{msg}</div>}
      {err && <div className="text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5 mb-2">{err}</div>}
      {rows.length === 0 ? (
        <div className="text-xs text-slate-500 bg-slate-900/40 border border-slate-800 rounded p-3 text-center">
          No purchases in the last 30 days.
        </div>
      ) : (
        <div className="space-y-1.5">
          {rows.map(r => (
            <div key={r.session_id} data-testid={`purchase-${r.sku}`}
              className="flex items-center justify-between bg-slate-900/40 border border-slate-800 rounded px-3 py-2 text-xs">
              <div className="flex-1 min-w-0">
                <div className="text-white font-bold truncate">{r.sku}</div>
                <div className="text-[10px] text-slate-500">{new Date(r.created_at).toLocaleString()} · ${r.amount?.toFixed(2)}</div>
              </div>
              {r.refunded_at ? (
                <span className="text-[10px] font-bold uppercase text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1">Refunded</span>
              ) : r.refundable ? (
                <button data-testid={`refund-${r.sku}`} onClick={() => refund(r)} disabled={busy === r.session_id}
                  className="px-2.5 py-1 text-[10px] font-bold rounded bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-200 disabled:opacity-50">
                  {busy === r.session_id ? "Refunding…" : `Refund · ${r.hours_left}h left`}
                </button>
              ) : (
                <span className="text-[10px] text-slate-600">Refund window closed</span>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="text-[10px] text-slate-600 italic mt-2">
        24-hour self-refund window. After that, message support. Refunds revoke the item and trigger a 7-day rebuy cooldown.
      </div>
    </div>
  );
}
